
import { Framebuffer } from '@engine/rendering/Framebuffer';
import { Matrix4f, Vector4f, ComputationalGeometryUtils } from '@engine/math';
import { AbstractTriangleRasterizer, TexturedAlphaBlendingTriangleRasterizer, TexturedTriangleRasterizer, TexturedTriangleRasterizer2D } from '@engine/rendering/rasterizer';
import { ClipMode, SutherlandHodgman2DClipper } from '@engine/rendering/clipping';
import { TextureCoordinate, Vertex } from '@engine/geometry/primitives';
import { AbstractRenderingPipeline } from '@engine/rendering/pipelines/AbstractRenderingPipeline';
import { TexturedMesh } from '@engine/rendering/pipelines/TexturedMesh';
import { projectToScreen } from '@engine/rendering/Projection';

export class TexturingRenderingPipeline extends AbstractRenderingPipeline {

    private sphereMapping: boolean = false;
    public triangleRasterizer: AbstractTriangleRasterizer | null = null;

    private vertexArray: Array<Vertex> = new Array<Vertex>(
        new Vertex(), new Vertex(), new Vertex()
    );
    setSphereMapping(sphereMapping: boolean) {
        this.sphereMapping = sphereMapping;
    }

    private modelViewMatrix: Matrix4f;

    private projectedVertices: Array<Vector4f> = new Array<Vector4f>(
        new Vector4f(0, 0, 0, 1), new Vector4f(0, 0, 0, 1), new Vector4f(0, 0, 0, 1)
    );

    private clipper: SutherlandHodgman2DClipper;

    constructor(framebuffer: Framebuffer) {
        super(framebuffer);
        this.setAlpha(1.0);
        this.triangleRasterizer = new TexturedTriangleRasterizer(framebuffer);
        this.clipper = new SutherlandHodgman2DClipper(framebuffer);
    }

    public setFramebuffer(framebuffer: Framebuffer) {
        this.framebuffer = framebuffer;
    }

    public enableAlphaBlending(): void {
        this.triangleRasterizer = new TexturedAlphaBlendingTriangleRasterizer(this.framebuffer, this);
    }

    public disablePerspectiveCorrectness() {
        this.triangleRasterizer = new TexturedTriangleRasterizer2D(this.framebuffer)
    }

    public disableAlphaBlending(): void {
        this.triangleRasterizer = new TexturedTriangleRasterizer(this.framebuffer);
    }

    public setModelViewMatrix(matrix: Matrix4f): void {
        this.modelViewMatrix = matrix;
    }

    public drawMeshArray(framebuffer: Framebuffer, meshes: Array<TexturedMesh>): void {
        for (let j: number = 0; j < meshes.length; j++) {
            const model: TexturedMesh = meshes[j];
            this.draw(framebuffer, model);
        }
    }

    public draw(framebuffer: Framebuffer, mesh: TexturedMesh): void {
        if (mesh.normals && mesh.normals2) {
            const normalMatrix: Matrix4f = this.modelViewMatrix.computeNormalMatrix();

            for (let i: number = 0; i < mesh.normals.length; i++) {
                normalMatrix.multiplyHomArr(mesh.normals[i], mesh.normals2[i]);
            }
        }

        for (let i: number = 0; i < mesh.points.length; i++) {
            this.modelViewMatrix.multiplyHomArr(mesh.points[i], mesh.points2[i]);
        }

        for (let i: number = 0; i < mesh.faces.length; i++) {
            const v1: Vector4f = mesh.points2[mesh.faces[i].vertices[0]];
            const v2: Vector4f = mesh.points2[mesh.faces[i].vertices[1]];
            const v3: Vector4f = mesh.points2[mesh.faces[i].vertices[2]];

            if (this.isInFrontOfNearPlane(v1) &&
                this.isInFrontOfNearPlane(v2) &&
                this.isInFrontOfNearPlane(v3)) {

                projectToScreen(this.framebuffer, v1, this.projectedVertices[0], true);
                projectToScreen(this.framebuffer, v2, this.projectedVertices[1], true);
                projectToScreen(this.framebuffer, v3, this.projectedVertices[2], true);

                if (this.isTriangleCCW(
                    this.projectedVertices[0],
                    this.projectedVertices[1],
                    this.projectedVertices[2])) {

                    this.vertexArray[0].projection = this.projectedVertices[0]; // p1 is Vector3f
                    this.vertexArray[1].projection = this.projectedVertices[1];
                    this.vertexArray[2].projection = this.projectedVertices[2];

                    if (this.sphereMapping && mesh.normals2) {
                        const n1: Vector4f = mesh.normals2[mesh.faces[i].normals[0]];
                        const n2: Vector4f = mesh.normals2[mesh.faces[i].normals[1]];
                        const n3: Vector4f = mesh.normals2[mesh.faces[i].normals[2]];

                        const e1 = mesh.points2[mesh.faces[i].vertices[0]];
                        const e2 = mesh.points2[mesh.faces[i].vertices[1]];
                        const e3 = mesh.points2[mesh.faces[i].vertices[2]];

                        // TDOD: remove this code and move after model view transformation
                        // and compute there the mesh.uv values
                        this.applySphereMapping(n1, e1, this.vertexArray[0]);
                        this.applySphereMapping(n2, e2, this.vertexArray[1]);
                        this.applySphereMapping(n3, e3, this.vertexArray[2]);

                        this.applyRefractionMapping(n1, e1, this.vertexArray[0]);
                        this.applyRefractionMapping(n2, e2, this.vertexArray[1]);
                        this.applyRefractionMapping(n3, e3, this.vertexArray[2]);
                    } else {
                        this.vertexArray[0].textureCoordinate = mesh.uv[mesh.faces[i].uv[0]];
                        this.vertexArray[1].textureCoordinate = mesh.uv[mesh.faces[i].uv[1]];
                        this.vertexArray[2].textureCoordinate = mesh.uv[mesh.faces[i].uv[2]];
                    }

 //new TriangleRasterizer33().rasterize(this.framebuffer, {v0:this.vertexArray[0],  v1:  this.vertexArray[1], v2:  this.vertexArray[2]})

                    this.clipConvexPolygon(framebuffer, this.vertexArray);
                }
            } else if (!this.isInFrontOfNearPlane(v1) &&
                !this.isInFrontOfNearPlane(v2) &&
                !this.isInFrontOfNearPlane(v3)) {
                continue;
            } else {
                this.vertexArray[0].position = v1; // v1 is Vector4f
                this.vertexArray[0].textureCoordinate = mesh.uv[mesh.faces[i].uv[0]];

                this.vertexArray[1].position = v2;
                this.vertexArray[1].textureCoordinate = mesh.uv[mesh.faces[i].uv[1]];

                this.vertexArray[2].position = v3;
                this.vertexArray[2].textureCoordinate = mesh.uv[mesh.faces[i].uv[2]];

                this.zClipTriangle(framebuffer, this.vertexArray);
            }
        }
    }

    private applySphereMapping(normal: Vector4f, eyeSpaceVertex: Vector4f, vertex: Vertex): void {
        const reflection = ComputationalGeometryUtils.reflectionVector(normal, eyeSpaceVertex);
        const coordinates = ComputationalGeometryUtils.sphereMapCoords(reflection);
        vertex.textureCoordinate.u = coordinates.u;
        vertex.textureCoordinate.v = coordinates.v;
    }

    private applyRefractionMapping(normal: Vector4f, eyeSpaceVertex: Vector4f, vertex: Vertex): void {
        const reflection = ComputationalGeometryUtils.reflectionVector(normal, eyeSpaceVertex);
        vertex.textureCoordinate.u = Math.min(Math.max(Math.round(vertex.projection.x + reflection.x * 35) / 319, 0), 1);
        vertex.textureCoordinate.v = Math.min(Math.max(Math.round(vertex.projection.y + reflection.y * 35) / 199, 0), 1);
    }

    public computeNearPlaneIntersection(p1: Vertex, p2: Vertex): Vertex {
        const ratio: number = (this.NEAR_PLANE_Z - p1.position.z) / (p2.position.z - p1.position.z);
        const vertex: Vertex = new Vertex();

        vertex.position = new Vector4f(
            ratio * (p2.position.x - p1.position.x) + p1.position.x,
            ratio * (p2.position.y - p1.position.y) + p1.position.y,
            this.NEAR_PLANE_Z
        );

        vertex.textureCoordinate = new TextureCoordinate(
            ratio * (p2.textureCoordinate.u - p1.textureCoordinate.u) + p1.textureCoordinate.u,
            ratio * (p2.textureCoordinate.v - p1.textureCoordinate.v) + p1.textureCoordinate.v
        );

        return vertex;
    }

    public zClipTriangle(framebuffer: Framebuffer, subject: Array<Vertex>): void {
        const input: Array<Vertex> = subject;
        const output: Array<Vertex> = new Array<Vertex>();
        let S: Vertex = input[input.length - 1];

        for (let i: number = 0; i < input.length; i++) {
            const point: Vertex = input[i];
            if (this.isInFrontOfNearPlane(point.position)) {
                if (!this.isInFrontOfNearPlane(S.position)) {
                    output.push(this.computeNearPlaneIntersection(S, point));
                }
                output.push(point);
            } else if (this.isInFrontOfNearPlane(S.position)) {
                output.push(this.computeNearPlaneIntersection(S, point));
            }
            S = point;
        }

        if (output.length < 3) {
            return;
        }

        for (let j: number = 0; j < output.length; j++) {
            projectToScreen(this.framebuffer, output[j].position, output[j].projection, true);
        }

        if (output.length === 3 &&
            !this.isTriangleCCW(output[0].projection, output[1].projection, output[2].projection)) {
            return;
        }

        if (output.length === 4 &&
            !this.isTriangleCCW2(
                output[0].projection,
                output[1].projection,
                output[2].projection,
                output[3].projection
            )) {
            return;
        }

        this.clipConvexPolygon(framebuffer, output);
    }


    public clipConvexPolygon(framebuffer: Framebuffer, subject: Array<Vertex>): void {
        this.clipper.setFramebuffer(framebuffer);
        const output = this.clipper.clipConvexPolygon(
            subject,
            ClipMode.PERSPECTIVE_TEXTURE,
        );

        if (output.length < 3) {
            return;
        }

        // triangulate new point set
        for (let i = 0; i < output.length - 2; i++) {
            this.triangleRasterizer.drawTriangleDDA(framebuffer, output[0], output[1 + i], output[2 + i]);
            //new TriangleRasterizer33().rasterize(this.framebuffer, {v0:  output[0],  v1:  output[1], v2:  output[2]})
        }
    }

}
